import React, { useState } from 'react';
import { Typography, Grid, Button as MuiButton } from '@mui/material';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { Markdown, Button, Section, SectionTitle } from 'shared-components';

/**
 * ColumnsSection - A flexible multi-column layout component with expandable content
 * 
 * Displays content in responsive columns with optional collapsible text sections.
 * Each column can have a tagline, title, expandable markdown content, and an optional CTA button.
 * 
 * @param {Object} props - Component props
 * @param {string} props.title - Main section title displayed at the top
 * @param {string} [props.introText] - Optional markdown-formatted introduction text displayed below the title
 * @param {Array<Object>} props.columns - Array of column objects to display
 * @param {string} props.columns[].tagline - Small overline text displayed above the column title
 * @param {string} props.columns[].title - Main heading for the column
 * @param {string|Array<string>} props.columns[].text - Markdown-formatted content. Can be a single string or array of strings
 * @param {boolean} [props.columns[].hidden] - If true, the text is initially collapsed with a "Mehr" (More) button to expand
 * @param {string} [props.columns[].buttonLabel] - Optional CTA button label
 * @param {string} [props.columns[].buttonHref] - Optional CTA button link URL
 * 
 * @example
 * <ColumnsSection
 *   title="Our Services"
 *   introText="Choose the service that fits your needs"
 *   columns={[
 *     {
 *       tagline: "Premium",
 *       title: "Professional Plan",
 *       text: "Full-featured professional service with **markdown** support",
 *       hidden: true,
 *       buttonLabel: "Learn More",
 *       buttonHref: "/professional"
 *     },
 *     {
 *       tagline: "Basic",
 *       title: "Starter Plan",
 *       text: ["First paragraph", "Second paragraph"],
 *       hidden: false
 *     }
 *   ]}
 * />
 */
export default function ColumnsSection({ title, introText, columns }) {
  const [columnVisibility, setColumnVisibility] = useState(
    columns.map(() => false)
  );

  const handleClick = (e) => {
    const index = Number(e.target.id);
    setColumnVisibility(prev => {
      const newVisibility = [...prev];
      newVisibility[index] = !newVisibility[index];
      return newVisibility;
    });
  };

  return (
    <Section>
      <SectionTitle>{title}</SectionTitle>
      {introText && <Markdown>{introText}</Markdown>}
      <Grid container spacing={2}>
        {columns.map(
          ({ tagline, title, text, hidden, buttonLabel, buttonHref }, i) => (
            <Grid
              key={i}
              size={{
                xs: 12,
                sm: 24 / columns.length,
                md: 12 / columns.length
              }}>
              <Typography variant="overline">{tagline}</Typography>
              <Typography variant="h5">{title}</Typography>
              <MuiButton
                variant="text"
                startIcon={<ArrowDownwardIcon />}
                id={i}
                onClick={handleClick}
                sx={{
                  display:
                    columnVisibility[i] || !hidden || !text
                      ? 'none'
                      : 'inline-flex',
                }}
              >
                Mehr
              </MuiButton>
              <span
                style={{
                  display: columnVisibility[i] || !hidden ? 'block' : 'none',
                }}
              >
                {Array.isArray(text) ? (
                  text.map((text, i) => <Markdown key={i}>{text}</Markdown>)
                ) : (
                  <Markdown key={i}>{text}</Markdown>
                )}
              </span>
              {buttonLabel && (
                <Button href={buttonHref} size="small" id={i} sx={{ mt: 2 }}>
                  {buttonLabel}
                </Button>
              )}
            </Grid>
          ),
        )}
      </Grid>
    </Section>
  );
}
